/**
 * A NUVEM — sessão, sincronismo e a decisão de usar ou não usar.
 *
 * Junta num objeto só as três coisas que os aplicativos precisam saber sobre o
 * servidor: se ele está configurado, quem está logado, e quando foi a última
 * vez que os dados subiram e desceram.
 *
 * ⚠️ A NUVEM É OPCIONAL, E ISSO É A ARQUITETURA, NÃO UMA FASE.
 *
 * Sem `VITE_SUPABASE_URL` configurada, `Nuvem.configurada` é falso e o sistema
 * roda exatamente como rodava antes: banco do navegador, tudo local, nada de
 * login. É esse o modo em que a Gráfica Boa União começa — um computador e os
 * tablets na mesma rede resolvem, e um sistema que exige servidor para abrir é
 * um sistema que para quando a internet cai.
 *
 * Com a URL configurada, a nuvem entra POR CIMA do banco local, nunca no lugar
 * dele: o apontamento continua sendo gravado no aparelho primeiro e só depois
 * empurrado. É a opção A do PRD §12.2, e é o que faz o tablet sobreviver a
 * duas horas sem rede sem ninguém perceber.
 *
 * ⚠️ POR QUE A SESSÃO É GUARDADA À MÃO, e não pelo SDK do Supabase. O tablet
 * fica preso à máquina e é reiniciado sem aviso; ele precisa voltar logado sem
 * que ninguém digite nada. Guardar `refresh_token` no depósito local e renovar
 * sozinho é meia dúzia de linhas e evita trazer o SDK inteiro — que, além do
 * peso, guarda a sessão no `localStorage`, que o navegador limpa em situações
 * que o IndexedDB sobrevive.
 */

import type { Deposito } from '@pcpgbu/dados';
import { Fila, type ItemDaFila } from './fila.js';
import { Sincronismo, type EstadoDoSincronismo } from './sincronismo.js';
import { ServidorSupabase } from './servidor-supabase.js';

export interface ConfiguracaoDaNuvem {
  url: string;
  chave: string;
}

export interface SessaoDaNuvem {
  token: string;
  renovacao: string;
  expiraEm: number;
  email: string;
  userId: string;
  /** Nome e foto vêm dos metadados do Supabase (o mesmo do cabeçalho da frota). */
  nome: string | null;
  avatarUrl: string | null;
}

const CHAVE_SESSAO = 'nuvem.sessao';

/** Renova com folga: token que expira no meio de um envio é falha evitável. */
const FOLGA_MS = 120_000;

export class Nuvem {
  readonly configurada: boolean;
  private sessao: SessaoDaNuvem | null = null;
  private sincronismo: Sincronismo | null = null;
  private renovando: Promise<void> | null = null;

  constructor(
    private readonly deposito: Deposito,
    private readonly cfg: ConfiguracaoDaNuvem | null,
    private readonly aoMudar: (e: EstadoDoSincronismo) => void = () => undefined,
    /** `true` no escritório: baixa a OS cheia; o tablet fica com a visão da produção. */
    private readonly escritorio = false,
  ) {
    this.configurada = !!cfg?.url && !!cfg?.chave;
  }

  /** Retoma a sessão guardada, se houver. Chamado uma vez no arranque. */
  async iniciar(): Promise<boolean> {
    if (!this.configurada) return false;
    this.sessao = this.lerSessaoGuardada();
    if (!this.sessao) return false;

    try {
      await this.tokenValido();
    } catch {
      /* Sessão velha demais: o aparelho volta deslogado, e é melhor assim do
         que voltar com um token que falha na primeira gravação. */
      await this.sair();
      return false;
    }
    this.montarSincronismo();
    return true;
  }

  /**
   * A sessão vive no `localStorage`, e não no depósito.
   *
   * ⚠️ POR QUE MUDOU (23/09/2026). Enquanto o escritório tinha banco local, a
   * sessão cabia lá. Como o sistema passou a ser 100% online — sem IndexedDB —,
   * o único lugar que sobra para o token é o armazenamento do navegador. É o que
   * todo app online da frota faz, e o que faz o F5 (recarregar) não pedir login
   * de novo.
   */
  private lerSessaoGuardada(): SessaoDaNuvem | null {
    try {
      const bruto = globalThis.localStorage?.getItem(CHAVE_SESSAO);
      return bruto ? (JSON.parse(bruto) as SessaoDaNuvem) : null;
    } catch {
      return null;
    }
  }

  private guardarSessao(): void {
    try {
      if (this.sessao) globalThis.localStorage?.setItem(CHAVE_SESSAO, JSON.stringify(this.sessao));
      else globalThis.localStorage?.removeItem(CHAVE_SESSAO);
    } catch {
      /* Armazenamento bloqueado: a sessão vale só nesta aba. */
    }
  }

  get logado(): boolean {
    return this.sessao !== null;
  }

  get email(): string | null {
    return this.sessao?.email ?? null;
  }

  get nome(): string | null {
    return this.sessao?.nome ?? null;
  }

  get avatarUrl(): string | null {
    return this.sessao?.avatarUrl ?? null;
  }

  get userId(): string | null {
    return this.sessao?.userId ?? null;
  }

  async entrar(email: string, senha: string): Promise<void> {
    if (!this.cfg) throw new Error('Nuvem não configurada.');

    const r = await fetch(`${this.cfg.url}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: { apikey: this.cfg.chave, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password: senha }),
    });
    const j = (await r.json()) as {
      access_token?: string;
      refresh_token?: string;
      expires_in?: number;
      user?: { id?: string; email?: string; user_metadata?: Record<string, unknown> };
      error_description?: string;
      msg?: string;
    };

    if (!j.access_token || !j.refresh_token) {
      /* A mensagem do Supabase é em inglês e técnica. Quem está na frente da
         tela quer saber se errou a senha ou se a internet caiu. */
      const bruta = j.error_description ?? j.msg ?? '';
      throw new Error(
        /invalid|credentials|grant/i.test(bruta)
          ? 'E-mail ou senha não conferem.'
          : `Não consegui entrar: ${bruta || 'o servidor não respondeu como esperado'}`,
      );
    }

    this.sessao = {
      token: j.access_token,
      renovacao: j.refresh_token,
      expiraEm: Date.now() + (j.expires_in ?? 3600) * 1000,
      email: j.user?.email ?? email,
      userId: j.user?.id ?? '',
      ...retratoDoUsuario(j.user?.user_metadata),
    };
    this.guardarSessao();
    this.montarSincronismo();
  }

  async sair(): Promise<void> {
    this.sessao = null;
    this.sincronismo = null;
    this.guardarSessao();
  }

  /**
   * O token de agora, renovado se estiver perto de expirar.
   *
   * ⚠️ UMA RENOVAÇÃO POR VEZ. Sem a trava, um sincronismo com cinco chamadas
   * paralelas dispararia cinco renovações; o Supabase invalida o
   * `refresh_token` usado, então da segunda em diante todas falhariam — e o
   * aparelho cairia sozinho, no meio do turno, sem ninguém ter feito nada.
   */
  private async tokenValido(): Promise<string> {
    if (!this.sessao || !this.cfg) throw new Error('Sem sessão.');
    if (Date.now() < this.sessao.expiraEm - FOLGA_MS) return this.sessao.token;

    this.renovando ??= this.renovar().finally(() => {
      this.renovando = null;
    });
    await this.renovando;
    if (!this.sessao) throw new Error('Sessão perdida na renovação.');
    return this.sessao.token;
  }

  private async renovar(): Promise<void> {
    if (!this.sessao || !this.cfg) throw new Error('Sem sessão.');

    const r = await fetch(`${this.cfg.url}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { apikey: this.cfg.chave, 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: this.sessao.renovacao }),
    });
    const j = (await r.json()) as {
      access_token?: string;
      refresh_token?: string;
      expires_in?: number;
    };
    if (!j.access_token || !j.refresh_token) throw new Error('Não consegui renovar a sessão.');

    this.sessao = {
      ...this.sessao,
      token: j.access_token,
      renovacao: j.refresh_token,
      expiraEm: Date.now() + (j.expires_in ?? 3600) * 1000,
    };
    this.guardarSessao();
  }

  private montarSincronismo(): void {
    if (!this.cfg) return;

    const servidor = this.criarServidor();

    this.sincronismo = new Sincronismo({
      deposito: this.deposito,
      fila: new Fila({ deposito: this.deposito }),
      servidor,
      /* ⚠️ INDEFINIDO É ONLINE, e não offline. O Node 21+ tem `navigator`
         global mas NÃO tem `onLine`; tratar indefinido como falso fazia o
         sincronismo dizer "Sem conexão" e não sair do lugar — fora do
         navegador, para sempre. Quem sabe que está sem rede é o navegador; na
         dúvida, tenta e deixa a falha de rede falar. */
      online: () => globalThis.navigator?.onLine !== false,
      aoMudar: this.aoMudar,
    });
  }

  /**
   * Põe uma ação na fila de saída.
   *
   * ⚠️ A CHAVE NASCE NO APARELHO, junto com a ação, e vai como `id` do item.
   * É o que faz reenviar ser inofensivo: o mesmo apontamento chegando duas
   * vezes é a mesma linha no servidor, não duas. Sem isso, um tablet que perde
   * a resposta e tenta de novo dobraria a produção do turno.
   *
   * ⚠️ E ELA NÃO FALHA QUANDO NÃO HÁ NUVEM. Sem servidor configurado a fila
   * simplesmente não existe, e a chamada não faz nada — quem chama não precisa
   * perguntar antes. O dado já está gravado no banco local de qualquer jeito;
   * a fila é só sobre CONTAR aos outros aparelhos.
   */
  async enfileirar(tipo: string, id: string, carga: unknown): Promise<void> {
    if (!this.configurada) return;
    await new Fila({ deposito: this.deposito }).enfileirar(tipo, carga, id);
  }

  /** Empurra o que está na fila e puxa o que mudou. Silencioso por design. */
  async sincronizar(): Promise<void> {
    if (!this.sincronismo || !this.sessao) return;
    await this.tokenValido();
    await this.sincronismo.sincronizar();
  }

  async estado(): Promise<EstadoDoSincronismo | null> {
    return this.sincronismo ? this.sincronismo.estado() : null;
  }

  /**
   * O cliente do servidor, com o token da sessão.
   *
   * Síncrono de propósito: o `Servidor` monta cabeçalho a cada chamada e não
   * pode esperar. A renovação acontece antes, em `sincronizar()`/`executar()`.
   */
  private criarServidor(): ServidorSupabase {
    if (!this.cfg) throw new Error('Nuvem não configurada.');
    return new ServidorSupabase({
      url: this.cfg.url,
      chaveAnonima: this.cfg.chave,
      tabelaOs: this.escritorio ? 'pcpgbu_os' : 'pcpgbu_os_producao',
      token: () => this.sessao?.token ?? null,
    });
  }

  /**
   * Sobe um arquivo (PDF da OS, imagem da faca) para o balde `pcpgbu` e devolve
   * o caminho, que é o que o banco guarda.
   */
  async enviarArquivo(tipo: string, dados: Uint8Array): Promise<string> {
    if (!this.sessao || !this.cfg) throw new Error('Sem sessão.');
    await this.tokenValido();
    const ext = tipo.includes('pdf') ? 'pdf' : tipo.includes('jpeg') || tipo.includes('jpg') ? 'jpg' : 'bin';
    const caminho = `${crypto.randomUUID()}.${ext}`;
    const r = await fetch(`${this.cfg.url}/storage/v1/object/pcpgbu/${caminho}`, {
      method: 'POST',
      headers: {
        apikey: this.cfg.chave,
        Authorization: `Bearer ${this.sessao.token}`,
        'Content-Type': tipo,
        'x-upsert': 'true',
      },
      body: new Blob([dados as BlobPart], { type: tipo }),
    });
    if (!r.ok) throw new Error(`Storage: HTTP ${r.status} ${(await r.text()).slice(0, 200)}`);
    return caminho;
  }

  /** Baixa um arquivo do balde pelo caminho. `null` se não existir. */
  async baixarArquivo(ref: string): Promise<{ tipo: string; dados: Uint8Array } | null> {
    if (!this.sessao || !this.cfg) return null;
    await this.tokenValido();
    const r = await fetch(`${this.cfg.url}/storage/v1/object/pcpgbu/${ref}`, {
      headers: { apikey: this.cfg.chave, Authorization: `Bearer ${this.sessao.token}` },
    });
    if (!r.ok) return null;
    return {
      tipo: r.headers.get('content-type') ?? 'application/octet-stream',
      dados: new Uint8Array(await r.arrayBuffer()),
    };
  }

  /**
   * Executa UMA ação no servidor, agora.
   *
   * ⚠️ É o caminho do escritório 100% online: sem banco local e sem fila, a
   * ação vai direto pela RPC. O erro sobe para quem chamou — é o que permite
   * avisar na tela em vez de engolir em silêncio. A chave é a mesma gerada no
   * aparelho, então repetir a chamada é inofensivo.
   */
  async executar(tipo: string, id: string, carga: unknown): Promise<void> {
    if (!this.sessao) throw new Error('Sem sessão.');
    await this.tokenValido();
    await this.criarServidor().subir([itemDaFila(tipo, id, carga)]);
  }

  /** O nome da organização atual — a empresa que veste este PCP. */
  async orgNome(): Promise<string | null> {
    if (!this.sessao || !this.cfg) return null;
    await this.tokenValido();
    return this.criarServidor().orgNome();
  }

  /**
   * Liga o sincronismo automático: ao voltar a rede, e a cada intervalo.
   *
   * Devolve a função que desliga — sem ela, uma tela que remonta deixaria dois
   * temporizadores rodando, e depois quatro.
   */
  ligarAutomatico(intervaloMs = 30_000): () => void {
    if (!this.configurada) return () => undefined;

    const tentar = (): void => {
      void this.sincronizar().catch(() => undefined);
    };
    const id = setInterval(tentar, intervaloMs);
    globalThis.addEventListener?.('online', tentar);
    tentar();

    return () => {
      clearInterval(id);
      globalThis.removeEventListener?.('online', tentar);
    };
  }
}

/** Um item de fila avulso, para o envio direto do escritório. */
function itemDaFila(tipo: string, id: string, carga: unknown): ItemDaFila {
  const agora = new Date().toISOString();
  return {
    id,
    tipo,
    carga,
    criadoEm: agora,
    tentativas: 0,
    proximaTentativaEm: agora,
    estado: 'pendente',
    ultimoErro: null,
  } as ItemDaFila;
}

/**
 * Nome e foto do usuário, tirados dos metadados do Supabase.
 *
 * O Supabase não tem um campo só para cada coisa: o nome vem em `full_name`
 * (e-mail/senha) ou `name` (Google), e a foto em `avatar_url` ou `picture`. É
 * a mesma leitura que o cabeçalho da frota faz.
 */
function retratoDoUsuario(meta: unknown): { nome: string | null; avatarUrl: string | null } {
  const m = (meta ?? {}) as Record<string, unknown>;
  const texto = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null);
  return {
    nome: texto(m['full_name']) ?? texto(m['name']),
    avatarUrl: texto(m['avatar_url']) ?? texto(m['picture']),
  };
}

/**
 * Lê a configuração do ambiente do Vite.
 *
 * Devolve `null` quando não há URL — e é esse `null` que mantém o sistema
 * inteiro funcionando sem servidor, sem nenhum `if` espalhado pelas telas.
 */
export function configuracaoDoAmbiente(
  env: Record<string, string | undefined>,
): ConfiguracaoDaNuvem | null {
  const url = env['VITE_SUPABASE_URL'];
  const chave = env['VITE_SUPABASE_ANON_KEY'];
  if (!url || !chave) return null;
  return { url: url.replace(/\/+$/, ''), chave };
}
